import { inject, Injectable } from '@angular/core';
import { Client, GetPeopleDataByEmailResponseDto } from '@fdm/api-client';
import { catchError, forkJoin, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class EmailExistenceService {
  private static readonly CACHE_TTL_MS = 5 * 60 * 1000;

  private _client = inject(Client);
  private _cache = new Map<string, { expiresAt: number; result$: Observable<boolean | null> }>();

  /** true: exists, false: not found, null: unknown (lookup failed). */
  exists(email: string | null | undefined): Observable<boolean | null> {
    const key = this._normalize(email);
    if (!key) {
      return of(null);
    }

    const cached = this._cache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.result$;
    }

    const result$ = this._lookup(key).pipe(
      tap(result => {
        if (result === null) {
          this._cache.delete(key);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this._cache.set(key, { expiresAt: Date.now() + EmailExistenceService.CACHE_TTL_MS, result$ });

    return result$;
  }

  private _lookup(email: string): Observable<boolean | null> {
    return this._client.email(email).pipe(
      map(people => (people ?? []).some(person => this._matches(person, email))),
      catchError(() => of(null)),
      switchMap(byDirectory => {
        if (byDirectory === true) {
          return of(true);
        }

        return forkJoin([
          this._client.sharedEmail(email).pipe(
            map(members => (members?.length ?? 0) > 0),
            catchError(() => of(null))
          ),
          this._client.peopleBriefData(email).pipe(
            map(person => !!person),
            catchError(error => of(error?.status === 404 ? false : null))
          ),
        ]).pipe(
          map(([isShared, isPersonal]) => {
            if (isShared || isPersonal) {
              return true;
            }

            return byDirectory === false && isShared === false && isPersonal === false ? false : null;
          })
        );
      })
    );
  }

  private _matches(person: GetPeopleDataByEmailResponseDto, email: string): boolean {
    return [person.email, person.preferredEmail, person.sharedEmail]
      .some(value => this._normalize(value) === email);
  }

  private _normalize(email: string | null | undefined): string {
    return (email ?? '').trim().toLowerCase();
  }
}
